import React, { useState, useEffect, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import './EntraAuth.css';

// Environment variables validation (removed debug logging for security)

// Validate required environment variables
const clientId = process.env.REACT_APP_ENTRA_CLIENT_ID;
const tenantId = process.env.REACT_APP_ENTRA_TENANT_ID;
const redirectUri = process.env.REACT_APP_ENTRA_REDIRECT_URI || `${window.location.origin}/login-entra`;

if (!clientId) {
    console.error('❌ REACT_APP_ENTRA_CLIENT_ID is not set!');
}
if (!tenantId) {
    console.error('❌ REACT_APP_ENTRA_TENANT_ID is not set!');
}
if (!redirectUri) {
    console.error('❌ REACT_APP_ENTRA_REDIRECT_URI is not set!');
}

// MSAL Configuration
const msalConfig = {
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: redirectUri,
        knownAuthorities: [`login.microsoftonline.com`],
        navigateToLoginRequestUrl: false, // Prevent automatic navigation
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        allowRedirectInIframe: false,
        allowNativeBroker: false,
        windowHashTimeout: 60000, // Increase timeout for slower networks
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0, // Disable iframe loading
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                // Added a default case to the switch statement to satisfy ESLint's default-case rule
                switch (level) {
                    case 0: // Error
                        console.error('MSAL Error:', message);
                        break;
                    case 1: // Warning
                        console.warn('MSAL Warning:', message);
                        break;
                    case 2: // Info
                        console.info('MSAL Info:', message);
                        break;
                    case 3: // Verbose
                        console.debug('MSAL Verbose:', message);
                        break;
                    default:
                        console.log('MSAL Log:', message);
                }
            },
            piiLoggingEnabled: false,
            logLevel: process.env.NODE_ENV === 'production' ? 1 : 2, // Warning in prod, Info in dev
        },
    },
};



const loginRequest = {
    scopes: ['openid', 'profile', 'email'],
    prompt: 'select_account',
    extraQueryParameters: {
        domain_hint: 'organizations'
    }
};

const graphTokenRequest = {
    scopes: ['User.Read'],
    forceRefresh: false
};

// Initialize MSAL instance
let msalInstance = null;

const initializeMsal = async () => {
    if (!msalInstance) {
        console.log('🔧 Creating MSAL instance...');
        msalInstance = new PublicClientApplication(msalConfig);

        console.log('🔧 Initializing MSAL...');
        try {
            await msalInstance.initialize();
            console.log('✅ MSAL initialized successfully');

            // NOTE: Removed cache clearing to preserve authentication state across page refreshes
            // Only clear cache during login if needed to prevent conflicts
        } catch (error) {
            console.error('❌ MSAL initialization failed:', error);
            throw error;
        }
    }
    return msalInstance;
};

// Enhanced login function with retry logic and fallback
const performLogin = async (instance, useRedirect = false, retryCount = 0) => {
    const maxRetries = 2;

    try {
        console.log(`🔄 Attempting login (attempt ${retryCount + 1}/${maxRetries + 1}) with ${useRedirect ? 'redirect' : 'popup'} flow...`);

        let response;
        if (useRedirect) {
            // Use redirect flow
            await instance.loginRedirect(loginRequest);
            // This will redirect, so we won't reach here
            return null;
        } else {
            // Try popup flow first
            response = await instance.loginPopup(loginRequest);
            console.log('✅ MSAL login popup successful:', response);
        }

        return response;
    } catch (error) {
        console.error(`❌ Login attempt ${retryCount + 1} failed:`, error);

        // Check if we should retry or fallback
        if (retryCount < maxRetries) {
            // Check error type to determine next action
            const isPopupBlocked = error.message && (
                error.message.includes('popup') ||
                error.message.includes('blocked') ||
                error.message.includes('user_cancelled') ||
                error.message.includes('interaction_in_progress')
            );

            if (isPopupBlocked && !useRedirect) {
                console.log('🔄 Popup blocked or failed, falling back to redirect flow...');
                return performLogin(instance, true, retryCount + 1);
            } else if (!isPopupBlocked && !useRedirect) {
                console.log('🔄 Retrying popup flow...');
                // Small delay before retry
                await new Promise(resolve => setTimeout(resolve, 1000));
                return performLogin(instance, false, retryCount + 1);
            }
        }

        // All retries exhausted or redirect failed
        throw error;
    }
};

// Refined EntraLoginButton to prevent multiple login windows and enable account selection
const EntraLoginButton = ({ disabled: externalDisabled = false }) => {
    const { instance } = useMsal();
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const handleLogin = async () => {
        if (isLoggingIn || externalDisabled) return; // Prevent multiple login attempts

        setIsLoggingIn(true);
        try {
            // Clear any existing accounts to force account selection
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
                console.log("Already logged in, clearing cache for account selection", accounts);
                // Clear cache to allow account selection
                await instance.clearCache();
            }

            // Use redirect flow with account selection prompt
            console.log("Starting redirect login with account selection...");
            await instance.loginRedirect(loginRequest);
            // Note: Code after this line won't execute due to redirect
        } catch (error) {
            console.error("Login failed", error);
        } finally {
            setIsLoggingIn(false);
        }
    };

    const isDisabled = isLoggingIn || externalDisabled;

    return (
        <button onClick={handleLogin} className="entra-login-button" disabled={isDisabled}>
            {isLoggingIn ? "Logging in..." : "Login with Entra"}
        </button>
    );
};

// Function to exchange Entra token with backend
const exchangeTokenWithBackend = async (accessToken, userInfo) => {
    try {
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/auth/entra/token-exchange`;

        console.log('🔄 Sending token exchange request to:', apiUrl);
        console.log('📋 Access token length:', accessToken.length);
        console.log('👤 User info:', userInfo);

        // Debug: Decode and log token details (first part only for security)
        try {
            const tokenParts = accessToken.split('.');
            if (tokenParts.length === 3) {
                const header = JSON.parse(atob(tokenParts[0]));
                const payload = JSON.parse(atob(tokenParts[1]));
                console.log('🔍 Token details:');
                console.log('   Header:', header);
                console.log('   Payload keys:', Object.keys(payload));
                console.log('   Issuer (iss):', payload.iss);
                console.log('   Audience (aud):', payload.aud);
                console.log('   Subject (sub):', payload.sub);
                console.log('   Expires:', new Date(payload.exp * 1000).toISOString());
            }
        } catch (decodeError) {
            console.warn('Could not decode token for debugging:', decodeError.message);
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                accessToken,
                userInfo,
            }),
        });

        console.log('📡 Token exchange response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Token exchange failed with response:', errorText);
            throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        console.log('✅ Token exchange successful, received data:', Object.keys(data));

        // Store the JWT token and user info
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('authType', 'entra');

        return data;
    } catch (error) {
        console.error('❌ Token exchange error:', error);
        throw error;
    }
};

// Entra Auth Provider Component
const EntraAuthProvider = ({ children }) => {
    const [msalInstance, setMsalInstance] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);

    useEffect(() => {
        const init = async () => {
            try {
                const instance = await initializeMsal();
                setMsalInstance(instance);
            } catch (error) {
                console.error('Failed to initialize MSAL in provider:', error);
            } finally {
                setIsInitializing(false);
            }
        };

        init();
    }, []);

    if (isInitializing || !msalInstance) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontFamily: 'Arial, sans-serif'
            }}>
                <div>Initializing authentication...</div>
            </div>
        );
    }

    return (
        <MsalProvider instance={msalInstance}>
            {children}
        </MsalProvider>
    );
};

// Hook for using Entra authentication
const useEntraAuth = () => {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);

    const getAccessToken = useCallback(async () => {
        try {
            const account = accounts[0];
            if (!account) {
                throw new Error('No account available');
            }

            // Try to get Microsoft Graph token first
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    ...graphTokenRequest,
                    account: account,
                });
                return tokenResponse.accessToken;
            } catch (graphError) {
                console.warn('Microsoft Graph token silent acquisition failed:', graphError.message);
                // Fall back to login scopes
                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: account,
                });
                return tokenResponse.accessToken;
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Fallback to interactive method
                try {
                    const tokenResponse = await instance.acquireTokenPopup({
                        ...graphTokenRequest,
                        account: accounts[0],
                    });
                    return tokenResponse.accessToken;
                } catch (graphPopupError) {
                    console.warn('Microsoft Graph token popup failed:', graphPopupError.message);
                    // Final fallback to login scopes
                    const tokenResponse = await instance.acquireTokenPopup(loginRequest);
                    return tokenResponse.accessToken;
                }
            }
            throw error;
        }
    }, [instance, accounts]);

    useEffect(() => {
        const handleAuthentication = async () => {
            // First priority: Check for stored authentication data
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const storedAuthType = localStorage.getItem('authType');

            if (storedToken && storedUser && storedAuthType === 'entra') {
                try {
                    setUser(JSON.parse(storedUser));
                    return;
                } catch (parseError) {
                    // Clear corrupted data
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('authType');
                }
            }

            // Second priority: Check MSAL authentication state
            if (isAuthenticated && accounts.length > 0) {
                const account = accounts[0];

                // Try silent token acquisition first to avoid popup
                try {
                    const accessToken = await getAccessToken();
                    const userInfo = {
                        id: account.localAccountId,
                        name: account.name,
                        username: account.username,
                        email: account.username,
                        entraRoles: [],
                    };

                    // Only attempt backend exchange if tokens are working
                    try {
                        const backendResponse = await exchangeTokenWithBackend(accessToken, userInfo);
                        setUser(backendResponse.user);
                    } catch (backendError) {
                        // If backend fails, use MSAL account info without backend data
                        // This prevents repeated authentication attempts
                        setUser({
                            id: account.localAccountId,
                            name: account.name,
                            email: account.username,
                            username: account.username,
                            role: 'user',
                        });
                    }
                } catch (tokenError) {
                    // If token acquisition fails (requires interaction), don't set user
                    // This prevents infinite retry loops
                    setUser(null);
                }
            } else {
                setUser(null);
            }
        };

        handleAuthentication();
    }, [isAuthenticated, accounts, getAccessToken]);

    const login = async () => {
        setLoading(true);
        try {
            const loginResponse = await performLogin(instance, false);

            if (loginResponse) {
                const userInfo = {
                    id: loginResponse.account.localAccountId,
                    name: loginResponse.account.name,
                    username: loginResponse.account.username,
                    email: loginResponse.account.username,
                    entraRoles: [],
                };

                const backendResponse = await exchangeTokenWithBackend(loginResponse.accessToken, userInfo);
                setUser(backendResponse.user);

                return backendResponse;
            }
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await instance.logoutPopup();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('authType');
            setUser(null);
        } catch (error) {
            console.error('Logout failed:', error);
            throw error;
        }
    };

    return {
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        getAccessToken,
    };
};

// Authentication status checker component
const EntraAuthStatus = () => {
    const { isAuthenticated, user } = useEntraAuth();
    const { accounts, inProgress } = useMsal();

    // Check localStorage for tokens
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const authType = localStorage.getItem('authType');

    // If we have tokens in localStorage, consider user authenticated
    const hasStoredAuth = token && storedUser && authType === 'entra';

    if (!isAuthenticated && !hasStoredAuth) {
        return (
            <div style={{
                padding: '10px',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                margin: '10px 0',
                fontSize: '14px'
            }}>
                <strong>❌ Not authenticated with Microsoft</strong>
                <div style={{ marginTop: '5px', fontSize: '12px' }}>
                    <div>MSAL Auth: {isAuthenticated ? '✅' : '❌'}</div>
                    <div>Stored Auth: {hasStoredAuth ? '✅' : '❌'}</div>
                    <div>In Progress: {inProgress}</div>
                    <div>Accounts: {accounts.length}</div>
                </div>
            </div>
        );
    }

    const account = accounts[0];
    const userData = storedUser ? JSON.parse(storedUser) : null;

    return (
        <div style={{
            padding: '10px',
            backgroundColor: '#e8f5e8',
            border: '1px solid #4caf50',
            borderRadius: '4px',
            margin: '10px 0',
            fontSize: '14px'
        }}>
            <strong>✅ Microsoft Authentication Status</strong>
            <div style={{ marginTop: '8px' }}>
                <div><strong>Name:</strong> {account?.name || user?.name || userData?.name}</div>
                <div><strong>Email:</strong> {account?.username || user?.email || userData?.email}</div>
                <div><strong>User ID:</strong> {user?.id || account?.localAccountId || userData?.id}</div>
                <div><strong>Role:</strong> {user?.role || userData?.role || 'Not set'}</div>
                <div><strong>Auth Type:</strong> {authType}</div>
                <div><strong>JWT Token:</strong> {token ? 'Present ✅' : 'Missing ❌'}</div>
                <div><strong>User Data:</strong> {storedUser ? 'Stored ✅' : 'Missing ❌'}</div>
                <div><strong>MSAL Accounts:</strong> {accounts.length}</div>
                <div><strong>MSAL Auth:</strong> {isAuthenticated ? '✅' : '❌'}</div>
            </div>
        </div>
    );
};

// Manual MSAL state reset component (for debugging)
const EntraResetButton = () => {
    const { instance } = useMsal();

    const handleReset = async () => {
        try {
            console.log('🔄 Manually resetting MSAL state...');

            if (instance) {
                await instance.clearCache();
                localStorage.clear(); // Clear all localStorage
                sessionStorage.clear(); // Clear all sessionStorage

                console.log('✅ MSAL state reset, reloading page...');
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            }
        } catch (error) {
            console.error('Failed to reset MSAL state:', error);
        }
    };

    return (
        <button
            onClick={handleReset}
            style={{
                padding: '5px 10px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                margin: '5px'
            }}
        >
            Reset Auth State
        </button>
    );
};

// Stub configuration checker component
const EntraConfigChecker = () => {
    // Return null to hide the warning message in the optimized build
    return null;
};

export {
    EntraAuthProvider,
    EntraLoginButton,
    useEntraAuth,
    EntraConfigChecker,
    EntraAuthStatus,
    EntraResetButton,
    exchangeTokenWithBackend,
};
